import type { IconTextBadgeItem } from '@/shared/ui/IconTextBadgeList';

import type { MlflowDatasetInput } from '@/entities/experiment';

import { getTagValue } from './tags';

export const formatDuration = (startTime: number, endTime?: number): string => {
  if (!endTime) return '-';
  const durationMs = endTime - startTime;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

export const getFileName = (path: string): string => {
  if (!path || path === '-') return '-';
  const parts = path.split('/');
  return parts[parts.length - 1] || path;
};

export const getGitCommitShort = (tags?: Array<{ key: string; value: string }>): string => {
  const commit = getTagValue(tags, 'mlflow.source.git.commit');
  if (commit === '-') return '-';
  return commit.substring(0, 7);
};

export const getDatasetContext = (datasetInput: MlflowDatasetInput): string => {
  const contextTag = datasetInput.tags?.find(
    (tag: { key: string; value: string }) => tag.key === 'mlflow.data.context'
  );
  return contextTag?.value || '';
};

export const formatDatasetBadgeItems = (datasetInputs: MlflowDatasetInput[]): IconTextBadgeItem[] => {
  return datasetInputs.map((input, index) => {
    const context = getDatasetContext(input);
    const contextText = context.charAt(0).toUpperCase() + context.slice(1);

    return {
      id: `${input.dataset.name}-${input.dataset.digest}-${index}`,
      icon: 'database' as const,
      text: `${input.dataset.name} (${input.dataset.digest})`,
      badge: contextText
        ? {
            text: contextText,
          }
        : undefined,
    };
  });
};

type ModelDisplay = {
  type: 'logged' | 'registered';
  name: string;
  version?: string;
  artifact_path?: string;
  run_id?: string;
  model_name?: string;
  linkTo?: string;
  url?: string;
};

export const formatModelBadgeItems = (models: ModelDisplay[]): IconTextBadgeItem[] => {
  return models.map((model, index) => ({
    id: `${model.type}-${model.name}-${index}`,
    icon: 'ml-model' as const,
    text: model.name,
    linkTo: model.linkTo,
    url: model.url,
    badge: model.version
      ? {
          text: model.version,
        }
      : undefined,
  }));
};

export type { ModelDisplay };
